import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from '../../../numberUtils';
import capitalize from '../../../stringUtils';
import dollar from '../../../../assets/images/icons/dollar.svg';
import mapIcon from '../../../../assets/images/icons/location.svg';
import travelPlane from '../../../../assets/images/icons/travel-plane.svg';
import userMapIcon from '../../../../assets/images/candidate-location.svg';
import workplaceIcon from '../../../../assets/images/worktype.svg';
import infoIcon from '../../../../assets/images/icons/info.svg';
import './ProjectBudgetLogisticsView.scss';

const imgTag = (icon, altText) => (
  <img className="me-2 mb-1" src={icon} alt={`${altText}`} />
);

function ProjectBudgetLogisticsView({
  employmentType,
  budgetMinimum,
  budgetMaximum,
  travelTime,
  travelLocation,
  workLocation,
  rateNegotiable,
  basePayHidden,
  renderEditLink,
  officeLocation,
  daysInOffice,
  remotePreferredLocation,
  showEditLink,
  compensationBenefitNote,
}) {
  const travelLocationText = () => (
    <div>
      <span className="mb-3">{imgTag(travelPlane, 'plane-icon')}</span>
      {`Travel Requirements: ${travelTime === 'travel' ? travelLocation[0] : 'N/A'}`}
    </div>
  );

  const workplaceType = () => {
    let workplaceText;
    if (workLocation === 'office') {
      workplaceText = 'In Office';
    } else if (!workLocation || workLocation === 'not sure') {
      workplaceText = 'To be determined';
    } else {
      workplaceText = capitalize(workLocation);
    }

    return (
      <div className="d-flex align-items-baseline">
        <span className="mb-3">{imgTag(workplaceIcon, 'location-icon')}</span>
        Workplace Type:
        {' '}
        {workplaceText}
        {daysInOffice ? `, ${daysInOffice} day(s) in office` : ''}
      </div>
    );
  };

  const officeLocationText = () => (
    ['hybrid', 'office', 'both'].includes(workLocation)
    && (
    <div className="d-flex align-items-baseline">
      <span className="mb-3">{imgTag(mapIcon, 'location-icon')}</span>
      Office Location:
      {' '}
      {officeLocation}
    </div>
    )
  );

  const candidateLocation = () => (
    workLocation === 'remote'
    && (
    <div className="d-flex align-items-baseline">
      <span className="mb-3">{imgTag(userMapIcon, 'location-icon')}</span>
      Desired Candidate Location:
      {' '}
      {remotePreferredLocation}
    </div>
    )
  );

  const displayBudget = () => (
    budgetMinimum && budgetMaximum
      ? `${formatCurrency(budgetMinimum, 0)} - ${formatCurrency(budgetMaximum, 0)}`
      : 'Budget'
  );

  const displayCompensationBenefitNote = () => (
    compensationBenefitNote
    && (
    <div className="compensation-benefit">
      <span className="mb-3">{imgTag(infoIcon, 'info')}</span>
      {compensationBenefitNote}
    </div>
    )
  );

  return (
    <div className="budget-and-logistics-container">
      <div className="header-container">
        <div className="title">Budget and Logistics</div>
        {showEditLink && renderEditLink()}
      </div>
      <div className="buget-and-logistics-body">
        <div className="d-flex align-items-baseline">
          <span className="mb-3">{imgTag(dollar, 'dollar-icon')}</span>
          {employmentType === 'full_time'
            ? `Annual Base Salary Range: ${displayBudget()} ${basePayHidden ? ' (Hidden from candidates)' : ''}`
            : `Hourly rate: ${displayBudget()} ${rateNegotiable ? ' (Negotiable)' : ''}`}
        </div>
        {displayCompensationBenefitNote()}
        {workplaceType()}
        {officeLocationText()}
        {candidateLocation()}
        {travelLocationText()}
      </div>
    </div>
  );
}

ProjectBudgetLogisticsView.propTypes = {
  travelTime: PropTypes.string,
  showEditLink: PropTypes.bool,
  daysInOffice: PropTypes.number,
  basePayHidden: PropTypes.bool,
  rateNegotiable: PropTypes.bool,
  renderEditLink: PropTypes.func,
  officeLocation: PropTypes.string,
  workLocation: PropTypes.string.isRequired,
  budgetMinimum: PropTypes.number.isRequired,
  budgetMaximum: PropTypes.number.isRequired,
  employmentType: PropTypes.string.isRequired,
  compensationBenefitNote: PropTypes.string.isRequired,
  remotePreferredLocation: PropTypes.string,
  travelLocation: PropTypes.arrayOf(PropTypes.string).isRequired,
};

ProjectBudgetLogisticsView.defaultProps = {
  travelTime: '',
  daysInOffice: null,
  officeLocation: '',
  showEditLink: false,
  basePayHidden: false,
  rateNegotiable: false,
  renderEditLink: () => {},
  remotePreferredLocation: '',
};

export default ProjectBudgetLogisticsView;
